import { useEffect, useRef, useState } from 'react';

const pathways = [
  {
    number: '01',
    title:  'Fostering Innovation',
    body:   'By addressing real-world conservation challenges, WITS pilots emerging technologies in practical settings — bridging the gap between research and field impact.',
  },
  {
    number: '02',
    title:  'Technology Adoption',
    body:   'WITS supports conservation practitioners in embedding technological tools into daily operations, ensuring they are effective and adaptable to on-the-ground realities.',
  },
  {
    number: '03',
    title:  'Usability & Scaling',
    body:   'With a focus on user-centered design, WITS is accessible to everyone from local community scouts to national policymakers, making its impact broad and replicable.',
  },
];

export default function AboutUs2() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="w-full bg-[#052e16] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* ── Text block ── */}
          <div className="flex flex-col gap-12">
            <div className={`reveal ${visible ? 'visible' : ''}`}>
              <span className="flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-green-500" />
                <span
                  className="text-green-400 text-xs tracking-[0.2em] uppercase font-medium"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  How We Work
                </span>
              </span>
              <h2
                className="text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-5"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Three pathways to
                <br />
                <span className="text-green-400">lasting conservation</span>
              </h2>
              <p
                className="text-white/60 text-base leading-relaxed max-w-md"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                WITS changes the narrative of conservation from anecdotal effort to evidence-based
                action, measured impact, and collective intelligence.
              </p>
            </div>

            {/* Pathway cards */}
            <div className="flex flex-col gap-4">
              {pathways.map((p, i) => (
                <div
                  key={p.number}
                  className={`flex gap-5 p-5 rounded-xl border border-white/8 bg-white/4 hover:bg-white/8 hover:border-green-500/30 transition-all duration-300 reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''}`}
                >
                  <span
                    className="shrink-0 text-green-500/50 text-xs font-bold mt-0.5"
                    style={{ fontFamily: 'var(--font-body)', letterSpacing: '0.1em' }}
                  >
                    {p.number}
                  </span>
                  <div>
                    <h4
                      className="text-white font-semibold mb-2 text-base"
                      style={{ fontFamily: 'var(--font-display)' }}
                    >
                      {p.title}
                    </h4>
                    <p
                      className="text-white/55 text-sm leading-relaxed"
                      style={{ fontFamily: 'var(--font-body)' }}
                    >
                      {p.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Image block ── */}
          <div className={`reveal reveal-delay-2 ${visible ? 'visible' : ''}`}>
            <div className="relative rounded-3xl overflow-hidden" style={{ boxShadow: '0 40px 80px rgba(0,0,0,0.5)' }}>
              <img
                src="/images/v1/img15.jpg"
                alt="Conservation field work"
                className="w-full h-[580px] object-cover"
              />
              {/* Green tint overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-[#052e16]/70 via-transparent to-transparent" />
              {/* Pull-quote */}
              <div
                className="absolute bottom-8 left-8 right-8 bg-[#052e16]/80 backdrop-blur-sm rounded-2xl p-5 border border-green-500/20"
              >
                <p
                  className="text-white/85 text-sm leading-relaxed italic"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  &ldquo;From anecdotal effort to evidence-based action &mdash; WITS makes every
                  data point count.&rdquo;
                </p>
                <p
                  className="text-green-400 text-xs mt-2 font-medium"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  LAPA Africa, 2024
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
